import type {
  Alerte,
  Brief,
  DeclarationTexte,
  MotInterdit,
  Provenance,
  TypeDouble,
} from "./histoires";

/**
 * Les regles du texte d'une double, partagees par le backend et le frontend du module Histoires
 * (plan, partie 2, decision 2) : le compteur de l'ecran, les alertes du chemin de fer et la
 * mesure H2 disent exactement la meme chose. Fonctions pures.
 */

/**
 * Les blancs, en echappements : les blancs ASCII (tabulation a retour chariot, espace), les
 * espaces Unicode et les caracteres de largeur nulle. Ils separent les mots. Avec
 * `INVISIBLES`, la meme liste que `PAS_UN_BLANC` du magasin (cote Postgres) et que `texte.py`
 * du PC : un test du backend et un test du PC les comparent a celles-ci.
 */
export const BLANCS =
  "\\u0009-\\u000d \\u0085\\u00a0\\u1680\\u180e\\u2000-\\u200d\\u2028\\u2029\\u202f\\u205f\\u2060\\u3000\\ufeff";

/**
 * Les caracteres invisibles qui ne sont pas des blancs : trait d'union conditionnel (U+00AD),
 * graphemes et remplissages sans trace (U+034F, U+115F, U+1160, U+17B4, U+17B5, U+3164,
 * U+FFA0), selecteurs de variante (U+180B a U+180D, U+FE00 a U+FE0F), marques et isolats de
 * direction (U+200E, U+200F, U+202A a U+202E, U+2066 a U+2069), operateurs invisibles et
 * caracteres de format (U+2061 a U+206F), blanc braille (U+2800). Une double qui n'a qu'eux
 * n'est pas ecrite (mesure H2), mais ils ne coupent pas un mot : un trait d'union
 * conditionnel au milieu d'un mot en laisse un seul. D'ou deux listes, et seul `estEcrite`
 * lit celle-ci.
 */
export const INVISIBLES =
  "\\u00ad\\u034f\\u115f\\u1160\\u17b4\\u17b5\\u180b-\\u180d\\u200e\\u200f\\u202a-\\u202e\\u2061-\\u206f\\u2800\\u3164\\ufe00-\\ufe0f\\uffa0";

const DES_BLANCS = new RegExp(`[${BLANCS}]+`, "u");
const UN_CARACTERE_ECRIT = new RegExp(`[^${BLANCS}${INVISIBLES}]`, "u");
const UNE_LETTRE_OU_UN_CHIFFRE = /[\p{L}\p{N}]/u;

/**
 * Une double est ecrite si son texte a au moins un caractere hors des blancs et des
 * invisibles (mesure H2).
 */
export function estEcrite(texte: string | null | undefined): boolean {
  return typeof texte === "string" && UN_CARACTERE_ECRIT.test(texte);
}

/**
 * Le nombre de mots : les morceaux separes par des blancs qui portent au moins une lettre ou un
 * chiffre. « peut-etre » et « l'enfant » comptent pour un ; la ponctuation isolee de la
 * typographie francaise (« , », :, !) ne compte pas.
 */
export function compterMots(texte: string | null | undefined): number {
  if (!texte) return 0;
  return texte.split(DES_BLANCS).filter((morceau) => UNE_LETTRE_OU_UN_CHIFFRE.test(morceau)).length;
}

/** Une occurrence d'un mot interdit dans le texte, en positions de caracteres JavaScript. */
export type Trouvaille = { readonly mot: string; readonly debut: number; readonly fin: number };

function echapper(motif: string): string {
  return motif.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Les apostrophes courbes et la lettre apostrophe (U+2018, U+2019, U+02BC). */
const UNE_APOSTROPHE = /[\u2018\u2019\u02bc]/gu;
/** Le trait d'union et le trait d'union insecable (U+2010, U+2011). */
const UN_TRAIT_D_UNION = /[\u2010\u2011]/gu;

/**
 * Les variantes d'un meme signe ramenees a une seule : les apostrophes valent l'apostrophe
 * droite, les traits d'union le trait d'union. Caractere pour caractere, sans changer la
 * longueur : les positions trouvees dans le texte uniformise restent celles du texte.
 */
function uniformiser(texte: string): string {
  return texte.replace(UNE_APOSTROPHE, "'").replace(UN_TRAIT_D_UNION, "-");
}

/**
 * Les occurrences des mots interdits du brief dans le texte : mots entiers, sans tenir compte de
 * la casse, une expression de plusieurs mots admise (ses blancs valent n'importe quels blancs).
 * Une apostrophe courbe vaut une apostrophe droite et un trait d'union insecable un trait
 * d'union, dans le texte comme dans le mot. Le mot est lu en NFC (un accent tape en deux
 * caracteres vaut le caractere accentue), et rendu ainsi ; un mot sans caractere ecrit est
 * ignore. Triees par position, la plus longue d'abord a position egale ; pour surligner,
 * l'ecran prend les positions telles quelles.
 *
 * Limites : le texte n'est pas normalise, parce que NFC en change la longueur et que l'ecran
 * surligne aux positions du texte tel qu'il est ecrit. Un texte en NFD (un accent combinant
 * apres sa lettre, comme en rendent certains copier-coller) ne se lit donc pas contre un mot
 * accentue. De meme, un trait d'union conditionnel (U+00AD) au milieu d'un mot, invisible a
 * l'ecran, le cache : « par », U+00AD, « tie » ne se lit pas contre « partie », ni l'inverse.
 */
export function motsInterditsDans(
  texte: string | null | undefined,
  interdits: readonly Pick<MotInterdit, "mot">[],
): Trouvaille[] {
  if (!texte) return [];
  const lu = uniformiser(texte);
  const trouvailles: Trouvaille[] = [];
  for (const { mot } of interdits) {
    const motif = mot.normalize("NFC").trim();
    if (!estEcrite(motif)) continue;
    const corps = uniformiser(motif)
      .split(DES_BLANCS)
      .filter(Boolean)
      .map(echapper)
      .join(`[${BLANCS}]+`);
    const expression = new RegExp(`(?<![\\p{L}\\p{N}])${corps}(?![\\p{L}\\p{N}])`, "giu");
    for (const trouve of lu.matchAll(expression)) {
      const debut = trouve.index ?? 0;
      trouvailles.push({ mot: motif, debut, fin: debut + trouve[0].length });
    }
  }
  return trouvailles.sort((a, b) => a.debut - b.debut || b.fin - a.fin);
}

/**
 * Les alertes d'une double `histoire` : les mots interdits trouves (chacun une fois, dans l'ordre
 * du texte), et le nombre de mots hors de la fourchette quand la double est ecrite. Une alerte
 * n'empeche jamais d'enregistrer.
 */
export function alertesDe(
  texte: string | null | undefined,
  brief: {
    readonly mots_interdits: readonly Pick<MotInterdit, "mot">[];
    readonly mots_par_double: Brief["mots_par_double"];
  },
): Alerte[] {
  const alertes: Alerte[] = [];
  const mots = [...new Set(motsInterditsDans(texte, brief.mots_interdits).map((t) => t.mot))];
  if (mots.length > 0) alertes.push({ type: "mot_interdit", mots });
  const fourchette = brief.mots_par_double;
  if (fourchette && estEcrite(texte)) {
    const nombre = compterMots(texte);
    if (nombre < fourchette.min || nombre > fourchette.max) {
      alertes.push({
        type: "hors_fourchette",
        mots: nombre,
        min: fourchette.min,
        max: fourchette.max,
      });
    }
  }
  return alertes;
}

/**
 * Les doubles dont le texte compte dans la declaration IA du livre (spec, section 2) : les
 * doubles `histoire` et la note pour les parents. Ce sont aussi les seules que Claude ecrit :
 * un texte de Claude ailleurs (une dedicace dans le liminaire) ne serait declare nulle part.
 */
export const TYPES_DECLARES: readonly TypeDouble[] = ["histoire", "note"];

/**
 * La declaration IA du texte d'un livre (spec, section 2) : `genere` des qu'une double
 * `histoire` ou `note` l'est, `assiste` si aucune ne l'est et qu'au moins une est `assiste`,
 * sinon `aucune`.
 */
export function declarationTexte(
  doubles: readonly { readonly type: TypeDouble; readonly provenance: Provenance | null }[],
): DeclarationTexte {
  const comptees = doubles.filter((d) => TYPES_DECLARES.includes(d.type));
  if (comptees.some((d) => d.provenance === "genere")) return "genere";
  if (comptees.some((d) => d.provenance === "assiste")) return "assiste";
  return "aucune";
}

/**
 * Le debut d'un texte, `longueur` unites UTF-16 au plus (la longueur de JavaScript et de Zod),
 * sans jamais couper une paire de substitution : un emoji coupe en deux laisserait une moitie
 * seule, que Postgres refuse dans du JSON et que l'ecran affiche en losange. Une longueur en
 * unites UTF-16 tient aussi une borne en caracteres, comme `length()` de Postgres.
 */
export function debutDe(texte: string, longueur: number): string {
  if (texte.length <= longueur) return texte;
  // Une premiere moitie de paire en derniere place : sa seconde est de l'autre cote de la coupe.
  const derniere = texte.charCodeAt(longueur - 1);
  const fin = derniere >= 0xd800 && derniere <= 0xdbff ? longueur - 1 : longueur;
  return texte.slice(0, fin);
}

/** Le debut du texte pour une carte du chemin de fer : 160 caracteres au plus, blancs reduits. */
export function extraitDe(texte: string | null | undefined, longueur = 160): string | null {
  if (!estEcrite(texte)) return null;
  const reduit = (texte as string).split(DES_BLANCS).filter(Boolean).join(" ");
  return reduit.length <= longueur ? reduit : `${debutDe(reduit, longueur - 3).trimEnd()}...`;
}
